import React, { useEffect, useRef, useState, memo, useMemo } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
import Text from "@components/AppText";
import BackArrow from "@assets/svg/BackArrow";
import colors from "@constants/color";
import metrics from "@constants/metrics";
import { format } from "date-fns";
import NoWithDrawals from "@assets/svg/NoWithDrawals";
import NoTransaction from "@assets/svg/NoTransaction";
import LeaderReferLink from "@components/Profile/LeaderReferLink";
import { actions } from "@reducers/orderReducer";
import TransactionEmpty from "./TransactionEmpty";
import { getWalletTransactionSummary } from "../../services/api";
import StatsCard from "./StatsCard";
import WithDrawalCard from "./WithdrawalCard";
import WalletFilters from "./WalletFilters";

import t from "@constants/localization";
import color from "@constants/color";

const PATH = "screens/Wallet/WalletDynamicScreen";

const WalletDynamicScreen = memo(({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [transactionSummary, setTransactionSummary] = useState(null);
  const { currentScreen, currentFilter, fundAccountsData } = useSelector(
    (state) => state.wallet,
  );
  const dispatch = useDispatch();

  const listData = transactionSummary ? transactionSummary.data : [];

  const limit = 100;
  const offset = useRef(0);

  useEffect(() => {
    fetchDataByType(true);
  }, []);

  const generatedManipulatedProps = useMemo(
    () => (data) => {
      return data.map((item) => {
        let props = {};

        if (currentScreen.type === "commission-credit") {
          props = {
            id: item.id,
            orderCommissionView: true,
            orderCommissionData: {
              username: item.customer_name,
              orderNumber: item.order_id,
              date: item.date,
              status: item.statusHi,
              statusKey: item.status,
            },
            secondaryStats: true,
            secondaryStatsData: Array.isArray(item.data)
              ? item.data.map((s) => ({
                  title: s.label,
                  value: s.value !== null ? `₹${s.value}` : "",
                }))
              : [],
          };
        } else if (
          currentScreen.type !== "commission-credit" &&
          currentScreen.type !== "withdrawal"
        ) {
          props = {
            id: item.id,
            otherCommissionsView: true,
            otherCommissionsData: {
              title: item.reason,
              commission: item.amount,
              date: item.date,
              status: item.statusHi,
              statusKey: item.status,
            },
          };
        } else if (currentScreen.type === "withdrawal") {
          props = {
            id: item.id,
            title: item.reason,
            value: item.amount,
            date: item.date,
            status: item.statusHi,
            statusKey: item.status,
            withdrawal_mode: item.withdrawal_mode,
            upi_address: item.upi_address,
            account_number: item.account_number,
            is_blacklisted: item.is_blacklisted,
            failure_reason: item.failure_reason,
            utr: item.utr,
            navigation,
            accounts: fundAccountsData,
          };
        }

        return props;
      });
    },
    [listData],
  );

  const getFormatedDate = (date) => {
    return format(new Date(date), "yyyy-MM-dd");
  };

  const fetchDataByType = async (
    firstCall = false,
    dateRange = null,
    shouldFetchDirectlyByDate = false,
  ) => {
    if (
      !firstCall &&
      transactionSummary &&
      transactionSummary.data &&
      transactionSummary.data.length >= transactionSummary.summary[0].count
    ) {
      return;
    }

    if (!loading) {
      setLoading(true);
    }

    const { toDate, fromDate } = currentFilter;

    let dates = {
      to: getFormatedDate(toDate),
      from: getFormatedDate(fromDate),
    };

    if (shouldFetchDirectlyByDate) {
      dates = {
        to: getFormatedDate(dateRange.to),
        from: getFormatedDate(dateRange.from),
      };
    }

    if (!firstCall) {
      offset.current += 100;
    }

    getWalletTransactionSummary({
      type: currentScreen.type,
      ...dates,
      limit,
      offset: offset.current,
    })
      .then(({ data }) => {
        let fullData = data;

        if (firstCall) {
          fullData = data;
        } else {
          if (!transactionSummary) {
            fullData = data;
          } else {
            fullData = {
              ...transactionSummary,
              data: [...transactionSummary.data, ...data.data],
            };
          }
        }

        setTransactionSummary(fullData);
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handleApplyFilter = ({ to, from }) => {
    offset.current = 0;

    fetchDataByType(true, { to, from }, true);
  };

  const isCloseToBottom = ({
    layoutMeasurement,
    contentOffset,
    contentSize,
  }) => {
    return layoutMeasurement.height + contentOffset.y >= contentSize.height;
  };

  const onScrollCustomerList = (e) => {
    if (!loading) {
      fetchDataByType(false);
    }
  };

  const summary = transactionSummary
    ? transactionSummary.summary[0].data
      ? transactionSummary.summary[0].data
      : transactionSummary.summary
    : {};

  const commission_credit_txt = transactionSummary
    ? transactionSummary.summary[0].commission_credit_txt
    : null;

  const DisplayCard = currentScreen.type
    ? currentScreen.type === "withdrawal"
      ? WithDrawalCard
      : StatsCard
    : null;

  const handleCardPress = (cardData) => {
    if (currentScreen.type === "commission-credit") {
      dispatch(
        actions.setSelectedOrder(
          cardData && parseInt(cardData.orderNumber, 10),
        ),
      );
      navigation.navigate("OrderDetails");
    }
  };

  let handlerProps = {};

  if (currentScreen.type === "commission-credit") {
    handlerProps = {
      handlePress: handleCardPress,
    };
  }

  return (
    <View style={styles.container}>
      <View style={styles.headContainer}>
        <View style={styles.beLikeRow}>
          <TouchableOpacity
            style={{ paddingHorizontal: 20, paddingVertical: 15 }}
            onPress={() => navigation.goBack()}
          >
            <BackArrow />
          </TouchableOpacity>
          <Text bold fontType="head2">
            {currentScreen.title}
          </Text>
        </View>
      </View>
      <WalletFilters
        containerBgColor="#fff"
        onApplyFilter={handleApplyFilter}
      />
      <FlatList
        data={generatedManipulatedProps(listData)}
        keyExtractor={(k) => k.id}
        ListEmptyComponent={() => (
          <View
            style={{
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {loading ? (
              <ActivityIndicator size="large" color={colors.primary} />
            ) : (
              <TransactionEmpty
                SvgComponent={
                  currentScreen.type === "withdrawal"
                    ? NoWithDrawals
                    : NoTransaction
                }
                message={t(`${PATH}/noTransactionsFound`)}
              />
            )}
          </View>
        )}
        contentContainerStyle={{
          minHeight:
            metrics.screenHeight -
            (currentScreen.type === "referral-commission-credit" ? 450 : 250),
          paddingBottom: 40,
          paddingHorizontal: 15,
        }}
        // onScroll={onScrollCustomerList}
        onEndReached={onScrollCustomerList}
        onEndReachedThreshold={1}
        scrollEventThrottle={16}
        ListHeaderComponent={() => (
          <>
            {currentScreen.type === "referral-commission-credit" && (
              <View
                style={{
                  paddingVertical: 20,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <LeaderReferLink navigation={navigation} />
              </View>
            )}
            {commission_credit_txt && (
              <View
                style={{
                  backgroundColor: color.white,
                  borderColor: color.plain_red,
                  borderWidth: 1,
                  borderRadius: 10,
                  marginVertical: 10,
                  paddingVertical: 12,
                  alignItems: "center",
                  flexDirection: "row",
                  paddingHorizontal: 12,
                }}
              >
                <Text
                  fontType="content2"
                  bold
                  style={{ color: color.plain_red }}
                >
                  {commission_credit_txt}
                </Text>
              </View>
            )}
            {Array.isArray(summary) && (
              <View
                style={[
                  styles.beLikeRow,
                  { margin: 10, justifyContent: "space-between" },
                ]}
              >
                {summary.map((s) => {
                  return (
                    <View key={s.key}>
                      <Text style={styles.secondaryText} fontType="content3">
                        {s.label}
                      </Text>
                      <Text
                        bold
                        style={[styles.text, { color: s?.color }]}
                        fontType="head2"
                      >
                        {`${s.key === "Delivered Customer" ? "" : "₹"}${s.value}`}
                      </Text>
                    </View>
                  );
                })}
              </View>
            )}
          </>
        )}
        renderItem={({ item }) => {
          return <DisplayCard {...item} {...handlerProps} />;
        }}
        ListFooterComponent={() => {
          return listData && listData.length && loading ? (
            <View style={{ paddingVertical: 20 }}>
              <ActivityIndicator size="small" color={colors.primary} />
            </View>
          ) : null;
        }}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  beLikeRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  container: {
    flex: 1,
    backgroundColor: "#F7F7F7",
  },
  headContainer: {
    backgroundColor: "#fff",
  },
  text: {
    color: colors.black,
  },
  secondaryText: {
    color: "rgba(0, 0, 0, 0.65)",
  },
});

export default WalletDynamicScreen;
